import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, XCircle } from "lucide-react";
import { Logo } from "@/components/logo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export function Verify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setError("This sign-in link is missing its code. Try the link from your email again.");
      return;
    }

    void (async () => {
      try {
        const res = await fetch("/api/auth/verify", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
          credentials: "include",
        });
        if (res.ok) {
          setStatus("success");
          void navigate("/dashboard", { replace: true });
        } else {
          setStatus("error");
          setError("This sign-in link is invalid or has expired. Request a new one to continue.");
        }
      } catch {
        setStatus("error");
        setError("We couldn't verify the link. Please try again.");
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-50 px-4 text-zinc-900">
      <div className="mx-auto mb-8 flex w-full max-w-5xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        <Logo showWordmark />
      </div>

      <Card className="w-full max-w-sm border-zinc-200 bg-white shadow-sm">
        <CardHeader className="text-center">
          <div
            className={`mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full ${
              status === "error" ? "bg-red-50" : "bg-emerald-50"
            }`}
          >
            {status === "error" ? (
              <XCircle size={22} className="text-red-600" />
            ) : (
              <MailCheck size={22} className="text-emerald-600" />
            )}
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {status === "verifying"
              ? "Signing you in…"
              : status === "success"
                ? "You're in"
                : "That link didn't work"}
          </CardTitle>
          <CardDescription>
            {status === "verifying"
              ? "One moment while we confirm your sign-in link."
              : status === "success"
                ? "Taking you to your computer."
                : error}
          </CardDescription>
        </CardHeader>
        {status === "verifying" && (
          <CardContent className="flex justify-center pb-8">
            <Loader2 size={24} className="animate-spin text-zinc-400" />
          </CardContent>
        )}
      </Card>
    </div>
  );
}
